/* KDO Z ANALÝZY JE SKUTEČNÁ OSOBA — jediný zdroj pravdy.
 * ============================================================================
 *
 * PROČ TENHLE SOUBOR VZNIKL (25. 8. 2026)
 * ---------------------------------------
 * Karta „Pojištění osob" (components/plan/PojisteniOsobCard.js) i prezentace
 * plánu (pages/PlanPrezentacePage.js) stavěly pole osob NATVRDO pro dva lidi:
 *
 *     const osoby = [
 *       { jmeno: d?.klienti?.[0]?.jmeno || "Klient 1", prijem: … },
 *       { jmeno: d?.klienti?.[1]?.jmeno || "Klient 2", prijem: … },  // vždycky
 *     ];
 *
 * U klienta, který přišel na schůzku SÁM (partner v analýze nevyplněný — prázdné
 * jméno i datum narození), ale kterému poradce vyplnil kolonku „Klient 2 — čistý
 * příjem", appka vymyslela druhou pojištěnou osobu a spočítala jí smrt
 * 2 665 684 Kč a invaliditu 6 672 747 Kč. Krytí pro člověka, který v analýze
 * není. Před klientem se to neobhájí a v nabídce pojišťovny takové pojištěné
 * nezaložíš.
 *
 * PRAVIDLO: OSOBU DĚLÁ IDENTITA, NE PŘÍJEM.
 * Samotné číslo v kolonce příjmu není člověk. Druhá osoba vzniká jen tehdy,
 * když má v analýze jméno, příjmení NEBO datum narození.
 *
 * ROZPOR SE NEZAMLČUJE.
 * Když příjem „Klient 2" vyplněný je a identita ne, vrací se `rozpor: true`.
 * Obrazovka poradce MUSÍ tenhle stav ukázat — tiché ignorování by bylo horší
 * než dnešek: poradce by nevěděl, proč druhá osoba zmizela.
 *
 * PŘÍJEM SE NEMAŽE (vědomé rozhodnutí).
 * Sporných 30 000 Kč zůstávají v příjmech domácnosti, tedy i v „příjmech, které
 * rodině zůstanou", ze kterých se počítá krytí PRVNÍ osoby. Důvod: nejčastější
 * příčina rozporu je, že partner reálně existuje a poradce jen nevyplnil jméno.
 * Odečíst příjem by v tom případě krytí živitele NAFOUKLO o výpadek, který
 * nenastane. Čísla první osoby se proto nemění vůbec — mizí jen vymyšlená
 * druhá osoba a poradce dostane hlášku, ať data srovná.
 */
import { asArr, asObj, asText } from "./bezpecneHodnoty";
/* Forma příjmu se v datech drží jako jedna hodnota bez ohledu na jazyk
 * (český popisek z FORMY_PRIJMU); starší slovenské zápisy převede tahle funkce. */
import { normFormaPrijmu } from "./analyzaV2/prezentace";
/* Invalidní důchod podle zákona (balík 4. 10. 2026): nárok normalizuje
 * tentýž modul, který z nich počítá (socialDavky nemá žádné importy → bez cyklu). */
import { normNarok } from "./socialDavky";
/* Razítko metodiky plánu (modul bez závislostí) — „partner bez data narození
 * = pojištění nespočteno“ platí jen plánu s razítkem ≥ 2026-10-13. */
import { platiZmena, verzePlanu, METODIKA_2026_10_13 } from "./metodikaPlanu";
/* Platné datum narození — TÁŽ definice jako brána průvodce (nález R5,
 * 14. 10. 2026; modul bez importů → bez cyklu). */
import { platneNarozeni } from "./datumNarozeni";

/** Neprázdný ořezaný text; objekt/pole/NaN → `""` (nikdy nespadne). */
function txt(v) {
  return asText(v).trim();
}

/** Číslo z pole analýzy; cokoli nečíselného → 0. */
function cislo(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Má osoba na indexu `i` v analýze IDENTITU (jméno / příjmení / narození)? */
export function maIdentitu(d, i) {
  const k = asObj(asArr(asObj(d).klienti)[i]);
  return !!(txt(k.jmeno) || txt(k.prijmeni) || txt(k.narozeni));
}

/** Je v analýze DRUHÁ osoba (partner)? Rozhoduje identita, ne příjem. */
export function maPartnera(d) {
  return maIdentitu(d, 1);
}

/** Měsíční příjem osoby 1 / 2 z `prijmy2` (prokazatelný + ostatní/OSVČ). */
export function prijemOsoby(d, i) {
  const p2 = asObj(asObj(d).prijmy2);
  return i === 0
    ? cislo(p2.prokaz1) + cislo(p2.ostatni1)
    : cislo(p2.prokaz2) + cislo(p2.ostatni2);
}

/** Je vyplněný příjem druhé osoby, která v analýze není? (= rozpor v datech) */
export function rozporPartnerBezIdentity(d) {
  return !maPartnera(d) && prijemOsoby(d, 1) > 0;
}

/* ===========================================================================
   DĚTI — STEJNÉ PRAVIDLO JAKO U PARTNERA (audit R6, 25. 8. 2026)
   ===========================================================================
 * Tlačítko „Přidat dítě" v analýze zakládá řádek
 *     { id, jmeno: "", prijmeni: <příjmení klienta>, narozeni: "" }
 * — tedy PŘEDVYPLNĚNÉ příjmení a jinak nic. Do 25. 8. 2026 se takový řádek
 * počítal jako plnohodnotné dítě:
 *     0 dětí → krytí smrti 8 410 554 Kč
 *     1 prázdný řádek → 7 445 110 Kč   (rozdíl 965 444 Kč K PODPOJIŠTĚNÍ)
 * Appka odečetla sirotčí důchod za dítě, které neexistuje, a karta navíc
 * nabídla dětské pojištění na 3 mil. Stačilo omylem kliknout — řádek nemá
 * žádnou validaci.
 *
 * PRAVIDLO: dítě dělá identita. Jméno NEBO datum narození NEBO příjmení,
 * které NENÍ jen to předvyplněné po rodiči. (Samotné příjmení po rodiči je
 * dar tlačítka, ne odpověď poradce — proto samo o sobě dítě nezakládá.)
 *
 * ROZPOR SE NEZAMLČUJE: `maDeteBezIdentity` říká obrazovce, že takový řádek
 * v datech je, aby poradce věděl, proč dítě do výpočtu nevstoupilo.
 */

/** Příjmení rodičů z analýzy (klienti[0], klienti[1]), lowercase, bez prázdných. */
function prijmeniRodicu(d) {
  const kl = asArr(asObj(d).klienti);
  return new Set([0, 1].map((i) => txt(asObj(kl[i]).prijmeni).toLowerCase()).filter(Boolean));
}

/** Má tenhle řádek dítěte identitu? `rodice` = výstup `prijmeniRodicu`. */
export function maIdentituDitete(dite, rodice = new Set()) {
  const k = asObj(dite);
  if (txt(k.jmeno) || txt(k.narozeni)) return true;
  const p = txt(k.prijmeni).toLowerCase();
  return !!p && !rodice.has(p);
}

/** Děti z analýzy, které jsou skutečné osoby (prázdné řádky vypadnou). */
export function detiZAnalyzy(d) {
  const rodice = prijmeniRodicu(d);
  return asArr(asObj(d).deti).filter((x) => maIdentituDitete(x, rodice));
}

/** Počet dětí, se kterými se počítá. */
export function pocetDeti(d) {
  return detiZAnalyzy(d).length;
}

/** Je v analýze řádek dítěte BEZ identity? (obrazovka to má poradci říct) */
export function maDeteBezIdentity(d) {
  const rodice = prijmeniRodicu(d);
  return asArr(asObj(d).deti).some((x) => !maIdentituDitete(x, rodice));
}

/* ===========================================================================
   OSVČ, NEBO ZAMĚSTNANEC? — ROZHODUJE POLE „FORMA PŘÍJMU“ (29. 9. 2026)
   ===========================================================================
 * Analýza má u každé osoby pole „Forma příjmu“ (`formaPrijmu1` / `formaPrijmu2`,
 * hodnoty FORMY_PRIJMU v lib/analyzaV2/prezentace.js). Do 29. 9. 2026 ho tenhle
 * soubor vůbec nečetl a OSVČ HÁDAL z toho, jestli „ostatní“ příjmy převyšují
 * prokazatelné. Poradce u živnostníka, jehož příjem zapsal do kolonky
 * prokazatelných, vybral „OSVČ“ — a engine mu přesto spočítal nemocenskou
 * zaměstnance. V ČR má OSVČ bez dobrovolného nemocenského pojištění
 * nemocenskou 0 (lib/socialDavky.js → `nemocenskaMes`), takže potřeba PN vyšla
 * PODHODNOCENÁ přesně o státní dávku, kterou klient nedostane.
 *
 * PRAVIDLO:
 *   „OSVČ“         → OSVČ, i když příjem leží v kolonce prokazatelných,
 *   „Zaměstnanec“  → zaměstnanec, i když má víc „ostatních“ příjmů (pronájem…),
 *   nic / „Majitel firmy“ / „Jiný zdroj“ / starý ručně psaný text → ODHAD.
 * Majitel firmy ani jiný zdroj o nemocenské nic neříkají (jednatel s odměnou
 * nemocensky pojištěný je, majitel žijící z podílů na zisku ne) — pro ně
 * zůstává dosavadní odhad z poměru příjmů, aby se krytí nezměnilo naslepo.
 *
 * ODHAD: převaha „ostatních“ příjmů nad prokazatelnými — táž heuristika, jaká
 * byla v obou obrazovkách zkopírovaná. Rovnost (obojí 0 nebo shodné částky)
 * NENÍ OSVČ: bez důkazu se klientovi nepodsouvá režim, který mu v pracovní
 * neschopnosti bere celou nemocenskou. */
const KLIC_FORMY = ["formaPrijmu1", "formaPrijmu2"];

/** Co o OSVČ říká pole „Forma příjmu“ osoby `i` (0 / 1):
 *  `true` = OSVČ, `false` = zaměstnanec, `null` = pole o tom nerozhoduje
 *  (nevyplněné, majitel firmy, jiný zdroj, neznámý starší zápis). */
export function osvcZFormyPrijmu(d, i) {
  const forma = normFormaPrijmu(asText(asObj(d)[KLIC_FORMY[i]]));
  if (forma === "OSVČ") return true;
  if (forma === "Zaměstnanec") return false;
  return null;
}

/** Odhad z poměru příjmů — jen když forma příjmu nerozhoduje. */
function osvcOdhadem(d, i) {
  const p2 = asObj(asObj(d).prijmy2);
  return i === 0
    ? cislo(p2.ostatni1) > cislo(p2.prokaz1)
    : cislo(p2.ostatni2) > cislo(p2.prokaz2);
}

/** Je osoba `i` OSVČ? Vyplněná forma příjmu má přednost, odhad je záloha. */
export function jeOsvc(d, i) {
  const zFormy = osvcZFormyPrijmu(d, i);
  return zFormy !== null ? zFormy : osvcOdhadem(d, i);
}

/* ===========================================================================
   PROFIL PRO INVALIDNÍ DŮCHOD (balík 4. 10. 2026, body 1–4 + varianta B)
   ===========================================================================
 * Nová pole u klienta v analýze (`klienti[i]`), jen pro český tenant:
 *   (pojistenVCrOd  „V Česku pojištěn/a od roku“ — majitel 4. 10. 2026 pole
 *                   zrušil („Úplně odstranit“). Počítá se vždy celý život
 *                   v ČR; uložený rok v datech zůstává, nic ho nečte.)
 *   (osvcMinimalni  „OSVČ platí jen minimální zálohy“ — majitel 5. 10. 2026
 *                   volbu zrušil („dej pryč“). OSVČ se počítá vždy z minimálních
 *                   záloh (konzervativně, bod 3); uložená odpověď v datech
 *                   zůstává, nic ji nečte.)
 *   narokInvalidni  „nárok na invalidní důchod“ ano / ne / nevím (jen osoba
 *                   BEZ PŘÍJMU; nezadáno = „nevím“ = bez nároku + štítek
 *                   „ověřit výpis ČSSZ“ — varianta B). U osoby s příjmem se
 *                   pole ignoruje: nárok se ověří z doby pojištění (§ 40),
 *                   aby zapomenuté „ne“ z doby rodičovské nevynulovalo důchod.
 * Rok narození a věk jdou z `narozeni` k datu `k` (datum analýzy). */

/* Platné datum z ISO textu / Date, jinak null (vlastní kopie — minimumSmrti
 * importuje tenhle soubor, opačný import by udělal cyklus). */
function datumZ(v) {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v !== "string" || !v.trim()) return null;
  const dt = new Date(v.trim().slice(0, 10));
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** Věk v celých letech k datu `k`; `null` = nezadáno / narození v budoucnu. */
function vekKDatu(narozeni, k) {
  const n = datumZ(narozeni);
  const ref = datumZ(k) || new Date();
  if (!n || n.getTime() > ref.getTime()) return null;
  let v = ref.getUTCFullYear() - n.getUTCFullYear();
  if (ref.getUTCMonth() < n.getUTCMonth() || (ref.getUTCMonth() === n.getUTCMonth() && ref.getUTCDate() < n.getUTCDate())) v -= 1;
  return Math.max(0, v);
}

/**
 * Vstupy invalidního důchodu osoby `i` pro `spocitejDavky`.
 * @param {object} d analýza
 * @param {number} i 0 / 1
 * @param {{k?: Date|string}} [opt] datum, ke kterému se počítá věk (výchozí datum analýzy, jinak dnes)
 * @returns {{rokNarozeni:number|null, vek:number|null, rokDnes:number,
 *            osvc:boolean, narokInvalidni:"ano"|"ne"|"nevim"|null}}
 */
export function profilDuchoduOsoby(d, i, opt = {}) {
  const k = asObj(asArr(asObj(d).klienti)[i]);
  const ref = datumZ(opt.k) || datumZ(asObj(d).datum) || new Date();
  const nar = datumZ(k.narozeni);
  const vek = nar ? vekKDatu(nar, ref) : null;
  const osvc = jeOsvc(d, i);
  const prijem = prijemOsoby(d, i);
  return {
    rokNarozeni: nar && vek != null ? nar.getUTCFullYear() : null,
    vek,
    rokDnes: ref.getUTCFullYear(),
    osvc,
    narokInvalidni: prijem > 0 ? null : (normNarok(k.narokInvalidni) || "nevim"),
  };
}

/**
 * Osoby pro `spocitejPojisteni` z dat analýzy.
 * Vrací JEDNU osobu, dokud partner v analýze skutečně není.
 * @param {object} d analýza
 * @param {object} popisky `{ klient1, klient2 }` — náhradní jména (přeložená)
 * @returns {{osoby: Array, maPartnera: boolean, rozpor: boolean, prijemBezOsoby: number}}
 */
export function osobyZAnalyzy(d, popisky = {}) {
  const jmeno = (i) => txt(asObj(asArr(asObj(d).klienti)[i]).jmeno)
    || (i === 0 ? (popisky.klient1 || "Klient 1") : (popisky.klient2 || "Klient 2"));
  /* Profil invalidního důchodu (rok narození, nárok) jde s osobou do
   * pojisteniEngine → `spocitejDavky` (balík 4. 10. 2026). OSVČ se počítá
   * vždy z minimálních záloh — volbu majitel 5. 10. 2026 zrušil. */
  const osoba = (i) => {
    const pr = profilDuchoduOsoby(d, i);
    return {
      jmeno: jmeno(i), prijem: prijemOsoby(d, i), osvc: jeOsvc(d, i),
      rokNarozeni: pr.rokNarozeni, vek: pr.vek, rokDnes: pr.rokDnes,
      narokInvalidni: pr.narokInvalidni,
    };
  };
  const osoby = [osoba(0)];
  const partner = maPartnera(d);
  if (partner) osoby.push(osoba(1));
  return {
    osoby,
    maPartnera: partner,
    rozpor: !partner && prijemOsoby(d, 1) > 0,
    prijemBezOsoby: partner ? 0 : prijemOsoby(d, 1),
  };
}

/* ===========================================================================
   PARTNER BEZ DATA NAROZENÍ = POJIŠTĚNÍ PARTNERA NESPOČTENO (13. 10. 2026)
   ===========================================================================
 * Majitel 12. 10. 2026, dotazník „Průvodce — datum partnera“: „Pustit
 * s důvodem (Doporučeno)“ — datum KLIENTA je povinné minimum plánu, u partnera
 * stačí důvod v bráně a pojištění partnera je „nespočteno“.
 *
 * CO DNES ENGINE S PARTNEREM BEZ DATA DĚLAL (zjištěno 13. 10. 2026):
 *   · planEngine smrt2/inval2 věk partnera nečte (horizont = roky do renty
 *     domácnosti) — čísla vyšla, jako by datum nebylo potřeba,
 *   · invalidní důchod partnera BEZ PŘÍJMU (varianta B) počítal zákon s věkem
 *     35 let (`socialDavky.invalidniDuchodCZ` → `chybiNarozeni`),
 *   · PN, denní odškodné, závažná onemocnění, trvalé následky (pojisteniEngine)
 *     i cena (pojistneOdhad) věk nečtou,
 *   · srovnání Před a po (srovnaniPojisteni) partnera bralo jako pojištěnou
 *     osobu a jeho starou smlouvu rušilo proti novému krytí.
 * Pojišťovna bez data narození nabídku nespočítá ani nepodepíše — krytí
 * i cena partnera byly odhad o člověku, kterého neznáme.
 *
 * PRAVIDLO (plán s razítkem ≥ 2026-10-13): partner je dál osobou analýzy
 * (příjem domácnosti, děti, jména na majetkových smlouvách), ale PLÁN HO
 * NEPOJIŠŤUJE: `osobyProPlan` ho z pojištěných osob vynechá a vrátí důvod
 * „chybiNarozeni“. Neznámé ≠ 0: výstupy nenapíšou 0 Kč, ale „nespočteno —
 * chybí datum narození“. Starší plán počítá postaru (pravidlo 3. 10. 2026).
 */

/** Důvod „pojištění partnera nespočteno“ (kód pro slovník/výstupy). */
export const PARTNER_NESPOCTENO = "chybiNarozeni";

/**
 * Je v analýze partner (identita) BEZ PLATNÉHO data narození?
 * Platné = RRRR-MM-DD, skutečný den v kalendáři, ne v budoucnu (k `dnes`) —
 * `lib/datumNarozeni.platneNarozeni`, TÁŽ funkce jako brána „Připraveno na
 * plán?“ (minimumPlanu). Dřív tu byl benevolentní `new Date(...)`: „1985“,
 * „1985-02-30“ nebo „3/15/1985“ brána hlásila jako chybějící datum, plán
 * partnera přesto pojistil (nález R5, 14. 10. 2026). Datum v budoucnu hlásí
 * průvodce jako jistě chybné (V10) — tady se bere jako nezadané.
 * @param {object} d analýza
 * @param {{dnes?: Date|string}} [opt]
 */
export function partnerBezNarozeni(d, opt = {}) {
  if (!maPartnera(d)) return false;
  const k = asObj(asArr(asObj(d).klienti)[1]);
  return !platneNarozeni(k.narozeni, datumZ(opt.dnes) || new Date());
}

/**
 * Nepočítá plán pojištění partnera? `"chybiNarozeni"` | `null`.
 * Jen plán s razítkem ≥ 2026-10-13 (nový plán i „Přepočítat“); starší plán
 * počítá postaru. Rozhoduje `d.navrh` (razítko), jako v celém plánu.
 * @param {object} d analýza (s `navrh`)
 * @param {{dnes?: Date|string}} [opt]
 */
export function nespoctenoPartnera(d, opt = {}) {
  if (!platiZmena(verzePlanu(asObj(d).navrh), METODIKA_2026_10_13)) return null;
  return partnerBezNarozeni(d, opt) ? PARTNER_NESPOCTENO : null;
}

/**
 * Osoby, které PLÁN POJIŠŤUJE — `osobyZAnalyzy` bez partnera, jehož pojištění
 * je nespočtené. Navíc `partnerNespocteno` (kód | null) a `jmenoPartnera`
 * pro hlášku „… nespočteno — chybí datum narození“. `maPartnera` zůstává
 * pravdivé (partner v analýze JE), `osoby.length` = pojištěné osoby.
 * Volají: karta Pojištění osob, cena ochrany, srovnání Před a po, prezentace.
 * @param {object} d analýza (s `navrh`)
 * @param {object} popisky `{ klient1, klient2 }`
 */
export function osobyProPlan(d, popisky = {}) {
  const v = osobyZAnalyzy(d, popisky);
  const duvod = nespoctenoPartnera(d);
  if (!duvod) return { ...v, partnerNespocteno: null, jmenoPartnera: "" };
  return {
    ...v,
    osoby: v.osoby.slice(0, 1),
    partnerNespocteno: duvod,
    jmenoPartnera: v.osoby[1] ? v.osoby[1].jmeno : (popisky.klient2 || "Klient 2"),
  };
}

/**
 * Podmínky bodů ZMENY_METODIKY (`tyka`, lib/metodikaPlanu.js) pro tuto
 * analýzu — pruh „Plán je spočítaný podle starší metodiky“ v Návrhu se ukáže,
 * jen když se změna klienta opravdu týká. Neznámý kód = týká se (raději pruh
 * navíc než tichá změna).
 * @param {object} d analýza
 * @returns {(kod: string) => boolean}
 */
export function tykaZmenyMetodiky(d) {
  return (kod) => {
    if (kod === "partnerBezNarozeni") return partnerBezNarozeni(d);
    /* 28. 10.: most do 60 se týká jen klienta s penzijkem. */
    if (kod === "penzijko") return asArr(asObj(asObj(d).majetek).penzijko).some((p) => (+asObj(p).hodnota || 0) > 0);
    /* 28. 10.: celé pojistné majetku z přebytku — jen když poradce zadal cenu majetkové smlouvy. */
    if (kod === "cenaMajetku") return Object.values(asObj(asObj(asObj(asObj(d).navrh).pojisteniMajetku).smlouvy)).some((s) => (+asObj(s).cena || 0) > 0);
    /* 29. 10.: nižší varianty mění PN a ZO — jen plán se zvolenou „Uskromnit se“ / „Řeším jen cenu“ a s výdělečnou osobou. */
    if (kod === "nizsiVarianty") {
      return ["uskromnit", "cena"].includes(asObj(asObj(d).navrh).varianta)
        && osobyZAnalyzy(d).osoby.some((o) => (+asObj(o).prijem || 0) > 0);
    }
    return true;
  };
}
